import { showDialog } from '@components/dialog';
import { Dropdown, MenuItem } from '@components/dropdown';
import {
  IcoArchive,
  IcoCollection,
  IcoDotsVertical,
  IcoFolder,
  IcoPlus,
  IcoTrash,
} from '@components/icons';
import { FolderFilter, ManagedFolder } from './state';

type Props = {
  folders: ManagedFolder[];
  current: FolderFilter;
  showArchived: boolean;
  canManage: boolean;
  onSelect(folderId: FolderFilter): void;
  onNewFolder?(): void;
  onRename?(folder: ManagedFolder): void;
  onDelete?(folder: ManagedFolder): void;
};

export function FolderList(props: Props) {
  return (
    <nav aria-label="Media organization" class="flex flex-col gap-5">
      <div class="flex flex-col gap-1">
        <NavItem
          icon={IcoCollection}
          label="All files"
          active={props.current === undefined}
          onClick={() => props.onSelect(undefined)}
        />
        <NavItem
          icon={IcoFolder}
          label="Unfiled"
          active={props.current === 'root'}
          onClick={() => props.onSelect('root')}
        />
        {props.showArchived && (
          <NavItem
            icon={IcoArchive}
            label="Archived"
            active={props.current === 'archived'}
            onClick={() => props.onSelect('archived')}
          />
        )}
      </div>
      <SidebarGroup label="Folders">
        {props.folders.map((folder) => (
          <NavItem
            key={folder.id}
            icon={IcoFolder}
            label={folder.name}
            active={props.current === folder.id}
            onClick={() => props.onSelect(folder.id)}
            menu={
              props.canManage && props.onRename && props.onDelete ? (
                <ItemMenu
                  label={folder.name}
                  onRename={() => props.onRename?.(folder)}
                  onDelete={() => confirmDeleteFolder(folder, props.onDelete!)}
                />
              ) : undefined
            }
          />
        ))}
        {props.onNewFolder && <AddButton label="New folder" onClick={props.onNewFolder} />}
      </SidebarGroup>
    </nav>
  );
}

function SidebarGroup({ label, children }: { label: string; children: preact.ComponentChildren }) {
  return (
    <section class="flex flex-col gap-1">
      <h3 class="px-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</h3>
      {children}
    </section>
  );
}

function NavItem({
  icon: Icon,
  label,
  active,
  onClick,
  menu,
}: {
  icon: typeof IcoFolder;
  label: string;
  active: boolean;
  onClick(): void;
  menu?: JSX.Element;
}) {
  return (
    <div
      class={`group flex items-center rounded-lg ${active ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}
    >
      <button
        type="button"
        class={`flex min-w-0 flex-1 items-center gap-2 px-2 py-2 text-left text-sm ${active ? 'font-medium text-indigo-800' : 'text-gray-700'}`}
        aria-pressed={active}
        title={label}
        onClick={onClick}
      >
        <Icon class={`size-4 shrink-0 ${active ? 'text-indigo-600' : 'text-gray-400'}`} />
        <span class="truncate">{label}</span>
      </button>
      {menu && <div class="pr-1">{menu}</div>}
    </div>
  );
}

function AddButton({ label, onClick }: { label: string; onClick(): void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      class="flex items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-medium text-indigo-600 hover:bg-indigo-50"
    >
      <IcoPlus class="size-4" />
      {label}
    </button>
  );
}

function ItemMenu({
  label,
  onRename,
  onDelete,
}: {
  label: string;
  onRename(): void;
  onDelete(): void;
}) {
  return (
    <Dropdown
      hideDownIcon
      triggerClass="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-white"
      renderMenu={() => (
        <div class="p-1 min-w-40 flex flex-col">
          <MenuItem class="px-3 py-2 text-left text-sm hover:bg-gray-50" onClick={onRename}>
            Rename folder
          </MenuItem>
          <MenuItem
            class="flex items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-gray-50"
            onClick={onDelete}
          >
            <IcoTrash class="size-4" /> Delete folder
          </MenuItem>
        </div>
      )}
    >
      <span class="sr-only">Actions for {label}</span>
      <IcoDotsVertical class="size-4" />
    </Dropdown>
  );
}

async function confirmDeleteFolder(
  folder: ManagedFolder,
  onDelete: (folder: ManagedFolder) => void,
) {
  const confirmed = await showDialog({
    mode: 'warn',
    title: `Delete "${folder.name}"?`,
    children: <p class="text-gray-600">Files in this folder will become Unfiled.</p>,
    confirmButtonText: 'Delete folder',
  });
  if (confirmed) {
    onDelete(folder);
  }
}
